import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import { mapPalette } from '../../../../core/globalVariables.js';
import { IoIosWarning } from "react-icons/io";
import { IoIosInformationCircle } from "react-icons/io";

const DynamicScatterChart = ({
    value,
    catgeoryPropertyName,
    metricKey,
    activeFeatureId,
    height = 160,
    globalState,
    globalRefs,
    selectedTimestampToEditFromScatterGraph,
    setSelectedTimestampToEditFromScatterGraph,
    setSelectedYearEditing
}) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const [clickedIndex, setClickedIndex] = useState(null);

    const selectedYearEditing = globalState.selectedYearEditing;
    const longstandingGeoJSONRef = globalRefs.longstandingGeoJSONRef;
    const originalGeoJSONforComparingRef = globalRefs.originalGeoJSONforComparingRef;

    const stringToHexColor = (str) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return mapPalette[Math.abs(hash) % mapPalette.length];
    };

    // --- Extract original feature timeseries ---

    const originalTimeMap = useMemo(() => {
        if (!value || !activeFeatureId || !originalGeoJSONforComparingRef?.current?.features) return {};
        const originalFeature = originalGeoJSONforComparingRef.current.features.find(
            f => f.skylark_id === activeFeatureId
        );
        return originalFeature?.feature_data_simple_timeseries?.[metricKey] || {};
    }, [value, originalGeoJSONforComparingRef, metricKey, activeFeatureId]);

    // --- Transform current and original timeseries into points ---

    const { normalPoints, originalPoints, categories } = useMemo(() => {
        if (!value) return { normalPoints: [], originalPoints: [], categories: [] };
        const entries = Object.entries(value).sort((a, b) => new Date(a[0]) - new Date(b[0]));
        const originalEntries = Object.entries(originalTimeMap).sort((a, b) => new Date(a[0]) - new Date(b[0]));
        const uniqueCategories = [
            ...new Set([...entries.map(([_, v]) => v), ...originalEntries.map(([_, v]) => v)])
        ];
        const normalPoints = entries.map(([ts, v]) => ({
            x: new Date(ts),
            y: uniqueCategories.indexOf(v),
            iso: ts,
            category: v,
        }));
        const originalPoints = originalEntries.map(([ts, v]) => ({
            x: new Date(ts),
            y: uniqueCategories.indexOf(v),
            iso: ts,
            category: v,
        }));
        return { normalPoints, originalPoints, categories: uniqueCategories };
    }, [value, originalTimeMap]);

    // --- Initialize Chart.js scatter chart ---

    const toIsoWithOffset = (date) => {
        const d = new Date(date);

        const pad = (n) => String(n).padStart(2, '0');

        const year = d.getUTCFullYear();
        const month = pad(d.getUTCMonth() + 1);
        const day = pad(d.getUTCDate());
        const hours = pad(d.getUTCHours());
        const minutes = pad(d.getUTCMinutes());
        const seconds = pad(d.getUTCSeconds());

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+00:00`;
    };

    useEffect(() => {
        if (!canvasRef.current || !normalPoints.length) return;
        chartRef.current?.destroy();
        const verticalLinePlugin = {
            id: 'verticalLinePlugin',
            afterDraw: (chart) => {
                if (!selectedYearEditing) return;
                const ctx = chart.ctx;
                const xScale = chart.scales.x;
                const yScale = chart.scales.y;

                const selectedTime = new Date(selectedYearEditing).getTime();
                const xPos = xScale.getPixelForValue(selectedTime);

                ctx.save();
                ctx.beginPath();
                ctx.moveTo(xPos, yScale.top);
                ctx.lineTo(xPos, yScale.bottom);
                ctx.lineWidth = 2;
                ctx.strokeStyle = '#29645D';
                ctx.setLineDash([5, 5]);
                ctx.stroke();
                ctx.restore();
            },
        };
        chartRef.current = new Chart(canvasRef.current, {
            type: 'scatter',
            data: {
                datasets: [
                    {
                        label: 'Current',
                        data: normalPoints,
                        // pointRadius: ctx => ctx.dataIndex === clickedIndex ? 6 : 3,
                        pointRadius: 5,
                        // pointBackgroundColor: ctx => {
                        //     const category = ctx.raw.category;
                        //     return ctx.dataIndex === clickedIndex ? '#fff' : stringToHexColor(category);
                        // },
                        pointBackgroundColor: ctx => {
                            const category = ctx.raw.category;
                            return ctx.dataIndex === clickedIndex ? stringToHexColor(category) : stringToHexColor(category);
                        },
                        pointBorderColor: ctx => stringToHexColor(ctx.raw.category),
                        // pointBorderWidth: 1.5,
                    },
                    {
                        label: 'Original',
                        data: originalPoints,
                        pointRadius: 3,
                        // pointBackgroundColor: ctx => stringToHexColor(ctx.raw.category),
                        // pointBorderColor: '#888',
                        pointBorderWidth: 1,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                scales: {
                    x: {
                        type: 'time',
                        time: { tooltipFormat: 'PP' },
                        ticks: { font: { size: 9 } },
                    },
                    y: {
                        ticks: {
                            font: { size: 9 },
                            callback: (value) => {
                                const label = categories[value] || '';
                                return label.length > 15 ? label.substring(0, 15) + '…' : label;
                            },
                        },
                    },
                },
                layout: {
                    padding: {
                        top: 10,
                        bottom: 10,
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                        labels: {
                            font: {
                                size: 6, // legend text size
                            },
                            usePointStyle: true, // enables point-style icons
                            pointStyle: 'circle', // makes them circular
                        },
                    },
                },
                onClick: (evt, elements) => {
                    if (!elements.length) return;

                    const chart = chartRef.current;
                    const { datasetIndex, index } = elements[0];
                    const pointData =
                        chart.data.datasets[datasetIndex].data[index];

                    if (!pointData) return;

                    if (datasetIndex === 0) {
                        setClickedIndex(index);

                        setSelectedTimestampToEditFromScatterGraph(pointData.iso);

                        const timestamp = toIsoWithOffset(pointData.iso);
                        // ✅ ADD THIS LINE
                        setSelectedYearEditing(
                            timestamp
                        );
                    }
                },
            },
            plugins: [verticalLinePlugin],
        });
        return () => chartRef.current?.destroy();
    }, [normalPoints, originalPoints, categories, selectedYearEditing, clickedIndex, longstandingGeoJSONRef]);

    const metricMissingAtSelectedTimestamp = useMemo(() => {
        if (!selectedYearEditing || !value) {
            return false;
        }
        const targetTime = new Date(selectedYearEditing).getTime();
        return !Object.entries(value).some(([ts, val]) =>
            new Date(ts).getTime() === targetTime &&
            val !== undefined &&
            val !== null &&
            val !== ""
        );
    }, [
        selectedYearEditing,
        value
    ]);

    return (
        <div style={{
            paddingTop: '0px',
            height: metricMissingAtSelectedTimestamp ? '175px' : '150px',
            paddingBottom: metricMissingAtSelectedTimestamp ? '50px' : '0px'
        }}>
            {metricMissingAtSelectedTimestamp && (
                <div
                    title="No data for this Value at this timestamp"
                    style={{
                        marginLeft: '0px',
                        textAlign: 'left',
                        zIndex: 10,
                        color: '#fff',
                        fontSize: '12px',
                        backgroundColor: '#989898',
                        paddingLeft: '10px',
                        marginLeft: '10px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        width: 'calc(100% - 30px)',
                        borderRadius: '3px',
                        marginBottom: '10px'
                    }}
                >
                    <IoIosInformationCircle
                        color="#fff"
                        size={20}
                        style={{ flexShrink: 0 }}
                    />

                    <span style={{
                        textAlign: 'left'
                    }}>
                        No <b>{catgeoryPropertyName}</b> data for selected<br />features at {' '}
                        <b>
                            {new Date(selectedYearEditing).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                            })}
                        </b>
                    </span>
                </div>
            )}
            <canvas ref={canvasRef} />
        </div>
    );
};

export default DynamicScatterChart;